import * as React from 'react';
import {
  root as rootCls,
  left as leftCls,
  leftButton as leftButtonCls,
  lockup as lockupCls,
  lockupBrand as brandCls,
  lockupProduct as productCls,
  right as rightCls,
} from './TopNav.css';
import { BrandMark } from '../../genesis/BrandMark/BrandMark';

export interface TopNavProps extends React.HTMLAttributes<HTMLElement> {
  /** Brand line (top of the lockup). Default "Agentis Ai". */
  brand?: string;
  /** Product line (bottom of the lockup, small uppercase tracked). Default "GENESIS". */
  product?: string;
  /**
   * Logo glyph slotted before the lockup. Defaults to the canonical
   * `<BrandMark size={32} />`, so the shell shows the full brand lockup
   * (mark + "Agentis Ai" / "GENESIS" wordmark) out of the box. Override
   * with a custom node, or pass `logo={null}` to suppress the mark.
   */
  logo?: React.ReactNode;
  /** Click handler on the brand lockup (typically navigates to workspace home). */
  onBrandClick?: () => void;
  /** Right-cluster slot — notify button + profile avatar trigger. */
  trailing?: React.ReactNode;
  /** Accessible name for the brand button when it's clickable. Default "Go to home". */
  brandAriaLabel?: string;
  /**
   * Accessible name for the `<header>` banner landmark. Default
   * "Application header" — gives the banner a name so screen-reader
   * landmark navigation distinguishes it (an unnamed `<header>` is a
   * bare, ambiguous "banner").
   */
  'aria-label'?: string;
  /** ref-as-prop. */
  ref?: React.Ref<HTMLElement>;
}

/**
 * TopNav — fixed 56px app top bar. White surface, brand lockup left,
 * notify + profile cluster right. The brand lockup becomes a real
 * focusable `<button>` (native `Enter`/`Space` activation + focus ring)
 * when `onBrandClick` is supplied; otherwise it's a non-interactive
 * `<div>`. The `<header>` banner carries an accessible name via
 * `aria-label`.
 *
 * Prototype: `http://localhost:8000/` `.top-nav`. Validated 2026-04-30.
 */
export function TopNav({
  brand = 'Agentis Ai',
  product = 'GENESIS',
  logo = <BrandMark size={32} />,
  onBrandClick,
  trailing,
  brandAriaLabel = 'Go to home',
  'aria-label': ariaLabel = 'Application header',
  ref,
  className,
  ...rest
}: TopNavProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  const lockup = (
    <>
      {logo}
      <div className={lockupCls}>
        <span className={brandCls}>{brand}</span>
        <span className={productCls}>{product}</span>
      </div>
    </>
  );
  return (
    <header ref={ref} className={cls} aria-label={ariaLabel} {...rest}>
      {onBrandClick ? (
        <button
          type="button"
          className={`${leftCls} ${leftButtonCls}`}
          aria-label={brandAriaLabel}
          onClick={onBrandClick}
        >
          {lockup}
        </button>
      ) : (
        <div className={leftCls}>{lockup}</div>
      )}
      <div className={rightCls}>{trailing}</div>
    </header>
  );
}
TopNav.displayName = 'TopNav';
